/**
 * The two dates a lease/deed form can work out for itself.
 *
 * Feedback #18 and #17. Both are DEFAULTS, not computed columns — the user can
 * always type over the result, and nothing recalculates on load, so an
 * existing row is never silently rewritten by opening its drawer.
 *
 * Checked against the live data before being written, which is also where the
 * "default, don't lock" decision comes from:
 *
 *   - Of 452 lease/deeds carrying a term, `effective_date` differs from
 *     `document_date` in ZERO of them — so defaulting one to the other is
 *     safe, and typing the document date twice was pure friction.
 *   - 451 of those 452 satisfy `expiration = effective + term`. The one that
 *     does not is a real lease, not a typo, which is exactly why this fills
 *     the field rather than owning it.
 */
import dayjs from "dayjs";

/** The `term_units` pick list (lease_deeds.term_units), mapped to dayjs. */
const UNIT_TO_DAYJS = {
  YEARS: "year",
  MONTHS: "month",
  DAYS: "day",
};

export const TERM_UNITS = Object.keys(UNIT_TO_DAYJS);

const isBlank = (v) => v === null || v === undefined || v === "";

/**
 * A positive whole number, or null.
 *
 * `term_length` is an integer column; a zero or negative term would compute an
 * expiration on or before the effective date, which is not a lease anyone
 * meant to describe — better to leave the field alone and let them finish
 * typing.
 */
const termLengthOf = (v) => {
  if (isBlank(v)) return null;
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
};

/**
 * Expiration = effective + term, or null when the inputs aren't all there yet.
 *
 * @param {*} effective    dayjs | Date | ISO string
 * @param {number|string} termLength
 * @param {string} termUnits  one of TERM_UNITS
 * @returns {object|null} a dayjs, so it drops straight into a DatePicker
 */
export const expirationFrom = (effective, termLength, termUnits) => {
  const unit = UNIT_TO_DAYJS[termUnits];
  const length = termLengthOf(termLength);
  if (!unit || length === null || isBlank(effective)) return null;
  const start = dayjs(effective);
  return start.isValid() ? start.add(length, unit) : null;
};

/**
 * What the form should fill in after a change.
 *
 * @param {object} changed  antd's `changedValues` — only the field(s) just touched
 * @param {object} all      antd's `allValues`
 * @returns {object} a patch for `form.setFieldsValue`; empty when there is
 *   nothing to do, so the caller can skip the call entirely and avoid the
 *   re-render.
 */
export const deriveLeaseDates = (changed = {}, all = {}) => {
  const patch = {};
  const touched = (field) =>
    Object.prototype.hasOwnProperty.call(changed, field);

  // #18 — Effective Date defaults to Document Date. Only when it is still
  // empty: once there is a value in the field it belongs to the user, and a
  // later edit of the document date must not reach over and change it.
  const effectiveWasBlank = isBlank(all.effective_date);
  if (touched("document_date") && effectiveWasBlank && !isBlank(all.document_date)) {
    patch.effective_date = dayjs(all.document_date);
  }

  // #17 — Expiration recalculates whenever one of its inputs changes. That is
  // the literal ask ("auto calculate based on Effective Date + Term Length &
  // Units"), and it is what makes correcting a term fix the expiry instead of
  // leaving a stale date behind. An override survives for as long as nobody
  // touches those three inputs again.
  const effective = patch.effective_date ?? all.effective_date;
  const inputTouched =
    touched("effective_date") ||
    touched("term_length") ||
    touched("term_units") ||
    // A document date that just seeded the effective date feeds the term too,
    // so one keystroke completes the whole chain.
    "effective_date" in patch;

  if (inputTouched) {
    const next = expirationFrom(effective, all.term_length, all.term_units);
    if (next) patch.expiration_date = next;
  }

  return patch;
};

export default deriveLeaseDates;
